// Tag inventory glyphs — status, battery and last-seen rendered as icons rather
// than text, mirroring trusstor_dashboard's admin panel. Outlines use the navy
// token via `currentColor` (set on the cell); battery bars carry their own tone
// tokens. Keep the 16×16 viewBox so they line up with the table's row height.

import type { TagStatus } from '@interfaces/IAdminTag';

type IGlyphProps = { size?: number };

const stroke = {
  stroke: 'currentColor',
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

function FullSignalIcon({ size = 16 }: IGlyphProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M5.8075 10.3789C6.39793 9.81489 7.18302 9.50018 7.99953 9.50018C8.81603 9.50018 9.60112 9.81489 10.1916 10.3789" {...stroke} />
      <path d="M3.68616 8.25754C4.83926 7.13093 6.38739 6.50018 7.99951 6.50018C9.61162 6.50018 11.1598 7.13093 12.3129 8.25754" {...stroke} />
      <path d="M1.56653 6.13622C3.28161 4.44702 5.59229 3.50018 7.99955 3.50018C10.4068 3.50018 12.7175 4.44702 14.4326 6.13622" {...stroke} />
      <path d="M8 13.25C8.41421 13.25 8.75 12.9142 8.75 12.5C8.75 12.0858 8.41421 11.75 8 11.75C7.58579 11.75 7.25 12.0858 7.25 12.5C7.25 12.9142 7.58579 13.25 8 13.25Z" fill="currentColor" />
    </svg>
  );
}

function SleepingIcon({ size = 16 }: IGlyphProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M13.8479 9.34799C12.8565 9.57541 11.8235 9.54726 10.8459 9.2662C9.86835 8.98513 8.97817 8.46035 8.25891 7.74109C7.53965 7.02182 7.01487 6.13165 6.7338 5.15406C6.45274 4.17647 6.42459 3.1435 6.65201 2.15206L6.65193 2.15204C5.60814 2.39282 4.64782 2.90862 3.8707 3.64589C3.09357 4.38315 2.52797 5.315 2.23261 6.34469C1.93725 7.37437 1.92291 8.46435 2.19107 9.50145C2.45923 10.5385 3.00012 11.485 3.75758 12.2424C4.51503 12.9999 5.46144 13.5408 6.49854 13.8089C7.53564 14.0771 8.62561 14.0628 9.6553 13.7674C10.685 13.472 11.6168 12.9064 12.3541 12.1293C13.0914 11.3522 13.6072 10.3919 13.848 9.34808L13.8479 9.34799Z" {...stroke} />
    </svg>
  );
}

function StorageIcon({ size = 16 }: IGlyphProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M13.5 3H2.5C2.22386 3 2 3.22386 2 3.5V5.5C2 5.77614 2.22386 6 2.5 6H13.5C13.7761 6 14 5.77614 14 5.5V3.5C14 3.22386 13.7761 3 13.5 3Z" {...stroke} />
      <path d="M13 6V12.5C13 12.6326 12.9473 12.7598 12.8536 12.8536C12.7598 12.9473 12.6326 13 12.5 13H3.5C3.36739 13 3.24021 12.9473 3.14645 12.8536C3.05268 12.7598 3 12.6326 3 12.5V6" {...stroke} />
      <path d="M6.5 8.5H9.5" {...stroke} />
    </svg>
  );
}

function NoSignalIcon({ size = 16 }: IGlyphProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M3 2.5L13 13.5" {...stroke} />
      <path d="M6.69556 3.59192C7.12749 3.53071 7.5632 3.50005 7.99945 3.50018C10.4078 3.4949 12.7204 4.44253 14.4325 6.13622" {...stroke} />
      <path d="M1.56653 6.13622C2.41554 5.29632 3.42128 4.63139 4.52663 4.1792" {...stroke} />
      <path d="M9.50745 6.68492C10.5661 6.94955 11.5347 7.49252 12.3128 8.25754" {...stroke} />
      <path d="M3.68616 8.25754C4.52878 7.42916 5.59323 6.86249 6.75093 6.62598" {...stroke} />
      <path d="M5.8075 10.3789C6.37113 9.84081 7.1129 9.52875 7.89166 9.50205C8.67043 9.47535 9.43183 9.73587 10.031 10.2341" {...stroke} />
      <path d="M7.99963 13.25C8.41385 13.25 8.74963 12.9142 8.74963 12.5C8.74963 12.0858 8.41385 11.75 7.99963 11.75C7.58542 11.75 7.24963 12.0858 7.24963 12.5C7.24963 12.9142 7.58542 13.25 7.99963 13.25Z" fill="currentColor" />
    </svg>
  );
}

/** Status → glyph. Off-site / unknown have no trusstor glyph; callers render a dash. */
export function TagStatusIcon({ status, size }: { status: TagStatus; size?: number }) {
  switch (status) {
    case 'Good':
      return <FullSignalIcon size={size} />;
    case 'Sleeping':
      return <SleepingIcon size={size} />;
    case 'Storage':
      return <StorageIcon size={size} />;
    case 'NoReception':
      return <NoSignalIcon size={size} />;
    default:
      return null;
  }
}

/** Battery glyph. Bars carry their own tone tokens; the casing follows `currentColor`. */
export function BatteryGlyph({ tone, size = 16 }: { tone: 'high' | 'medium' | 'low'; size?: number }) {
  const barColor = tone === 'high' ? 'var(--green)' : tone === 'medium' ? 'var(--amber)' : 'var(--red)';
  const bar = { stroke: barColor, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M6 1L10 1" {...stroke} />
      <path d="M11.5 3L4.5 3C3.94772 3 3.5 3.44772 3.5 4L3.5 13.5C3.5 14.0523 3.94772 14.5 4.5 14.5L11.5 14.5C12.0523 14.5 12.5 14.0523 12.5 13.5L12.5 4C12.5 3.44771 12.0523 3 11.5 3Z" {...stroke} />
      <path d="M10.5 12.2L5.5 12.2" {...bar} />
      <path d="M10.5 9.9L5.5 9.9" {...bar} />
      {tone !== 'low' && <path d="M10.5 7.6L5.5 7.6" {...bar} />}
      {tone === 'high' && <path d="M10.5 5.3L5.5 5.3" {...bar} />}
    </svg>
  );
}

/** Last-seen recency glyph: clear pin (recent) → crossed (a while) → half-faded (long ago). */
export function LastSeenIcon({ tone, size = 16 }: { tone: 'ok' | 'warning' | 'alert'; size?: number }) {
  if (tone === 'ok') {
    return (
      <svg width={size} height={size} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M12.3008 6.70973C12.3008 10.5805 7.99995 13.5911 7.99995 13.5911C7.99995 13.5911 3.6991 10.5805 3.6991 6.70973C3.6991 5.56907 4.15222 4.47513 4.95879 3.66857C5.76535 2.862 6.85929 2.40887 7.99995 2.40887C9.14061 2.40887 10.2345 2.862 11.0411 3.66857C11.8477 4.47513 12.3008 5.56907 12.3008 6.70973V6.70973Z" stroke="currentColor" strokeWidth="0.809572" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M8 8.43012C8.95012 8.43012 9.72035 7.6599 9.72035 6.70978C9.72035 5.75966 8.95012 4.98944 8 4.98944C7.04989 4.98944 6.27966 5.75966 6.27966 6.70978C6.27966 7.6599 7.04989 8.43012 8 8.43012Z" stroke="currentColor" strokeWidth="0.809572" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  if (tone === 'warning') {
    return (
      <svg width={size} height={size} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M12.3008 6.70973C12.3008 10.5805 7.99995 13.5911 7.99995 13.5911C7.99995 13.5911 3.6991 10.5805 3.6991 6.70973C3.6991 5.56907 4.15222 4.47513 4.95879 3.66857C5.76535 2.862 6.85929 2.40887 7.99995 2.40887C9.14061 2.40887 10.2345 2.862 11.0411 3.66857C11.8477 4.47513 12.3008 5.56907 12.3008 6.70973V6.70973Z" stroke="currentColor" strokeWidth="0.809572" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M8 8.43012C8.95012 8.43012 9.72035 7.6599 9.72035 6.70978C9.72035 5.75966 8.95012 4.98944 8 4.98944C7.04989 4.98944 6.27966 5.75966 6.27966 6.70978C6.27966 7.6599 7.04989 8.43012 8 8.43012Z" stroke="currentColor" strokeWidth="0.809572" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M3.55151 2.07715L13.5515 13.0771" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M4.95257 3.55762C4.14436 4.36583 3.69031 5.46201 3.69031 6.605C3.69031 10.4837 7.99995 13.5004 7.99995 13.5004C7.99995 13.5004 9.62371 12.3638 10.8778 10.5889" stroke="currentColor" strokeWidth="0.811227" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M9.24078 7.80124C9.08358 7.96434 8.89579 8.09488 8.68813 8.18541C8.48048 8.27594 8.25702 8.32468 8.03053 8.32885C7.80404 8.33302 7.57895 8.29254 7.3681 8.20972C7.15725 8.1269 6.96478 8.00336 6.80168 7.84615C6.63857 7.68895 6.50803 7.50116 6.4175 7.2935C6.32698 7.08585 6.27824 6.8624 6.27407 6.6359C6.26989 6.40941 6.31038 6.18432 6.3932 5.97347C6.47602 5.76262 6.59956 5.57015 6.75676 5.40705" stroke="currentColor" />
      <path d="M3.55151 2.07715L13.5515 13.0771" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
